import { useState, useEffect, useCallback, useRef } from 'react';
import { HelpCircle, ChevronRight, ChevronLeft, X, Sparkles, BookOpen } from 'lucide-react';
import { theme } from '../../lib/theme';

/**
 * GuidedTour — Polished step-by-step walkthrough for Trovva
 *
 * Features:
 *   - Smooth animated spotlight that transitions between elements
 *   - Dark overlay dims everything except the highlighted area
 *   - Detailed tooltip with step number, icon, progress bar
 *   - Keyboard navigation (← → Escape)
 *   - Auto-scroll into view
 *   - Centered modals for welcome/completion steps (target: null)
 *
 * Usage:
 *   <GuidedTour tourId="dashboard" steps={DASHBOARD_STEPS} />
 */

// ── Smooth Spotlight Overlay ─────────────────────────────────
function SpotlightOverlay({ targetRect, onClick }) {
  // When no target, full dark overlay (for centered modals)
  if (!targetRect) {
    return (
      <div onClick={onClick} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', zIndex: 9998,
        transition: 'opacity 0.4s ease',
      }} />
    );
  }

  const pad = 10;
  const r = {
    x: targetRect.left - pad,
    y: targetRect.top - pad,
    w: targetRect.width + pad * 2,
    h: targetRect.height + pad * 2,
  };

  return (
    <div onClick={onClick} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998,
    }}>
      <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={r.x} y={r.y} width={r.w} height={r.h}
              rx="12" fill="black"
              style={{ transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}
            />
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>
      {/* Accent ring around target */}
      <div style={{
        position: 'fixed',
        top: r.y, left: r.x, width: r.w, height: r.h,
        borderRadius: 12,
        border: `2px solid ${theme.colors.accent}`,
        boxShadow: `0 0 0 4px ${theme.colors.accent}25, 0 0 30px ${theme.colors.accent}15`,
        pointerEvents: 'none',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }} />
    </div>
  );
}


// ── Tour Tooltip ─────────────────────────────────────────────
function TourTooltip({ step, stepIndex, totalSteps, targetRect, onNext, onPrev, onSkip, onDone }) {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const isCentered = !targetRect;
  const tooltipRef = useRef(null);

  let style = {};
  let arrowStyle = null; // arrow pointing from tooltip to target
  let resolvedPos = null; // actual placement after flip

  if (isCentered) {
    style = {
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      width: 460, maxWidth: 'calc(100vw - 32px)',
    };
  } else {
    let pos = step.position || 'bottom';
    const gap = 18;
    const tooltipW = 480;
    const tooltipEstH = 200; // estimated tooltip height for flip check
    const pad = 10; // accent ring padding

    // Clamp left position to stay on screen
    const clampLeft = (idealLeft) => Math.max(16, Math.min(idealLeft, window.innerWidth - tooltipW - 16));

    // Auto-flip: if tooltip would go below viewport, flip to top (and vice versa)
    if (pos === 'bottom' && targetRect.bottom + gap + pad + tooltipEstH > window.innerHeight) {
      pos = 'top';
    } else if (pos === 'top' && targetRect.top - gap - pad - tooltipEstH < 0) {
      pos = 'bottom';
    }

    resolvedPos = pos;
    const centerX = targetRect.left + targetRect.width / 2;
    const tooltipLeft = clampLeft(centerX - tooltipW / 2);
    // Arrow horizontal position relative to tooltip
    const arrowLeft = Math.max(20, Math.min(centerX - tooltipLeft, tooltipW - 20));

    if (pos === 'bottom') {
      style = {
        position: 'fixed',
        top: targetRect.bottom + gap + pad,
        left: tooltipLeft,
        width: tooltipW,
      };
      arrowStyle = {
        position: 'absolute', top: -8, left: arrowLeft,
        width: 16, height: 8, transform: 'translateX(-50%)',
      };
    } else if (pos === 'top') {
      style = {
        position: 'fixed',
        bottom: window.innerHeight - targetRect.top + gap + pad,
        left: tooltipLeft,
        width: tooltipW,
      };
      arrowStyle = {
        position: 'absolute', bottom: -8, left: arrowLeft,
        width: 16, height: 8, transform: 'translateX(-50%) rotate(180deg)',
      };
    } else if (pos === 'right') {
      const topVal = Math.max(16, Math.min(targetRect.top + targetRect.height / 2 - 100, window.innerHeight - tooltipEstH - 16));
      style = {
        position: 'fixed',
        top: topVal,
        left: Math.min(targetRect.right + gap + pad, window.innerWidth - tooltipW - 16),
        width: tooltipW,
      };
      const arrowTop = targetRect.top + targetRect.height / 2 - topVal;
      arrowStyle = {
        position: 'absolute', left: -8, top: Math.max(20, Math.min(arrowTop, tooltipEstH - 20)),
        width: 8, height: 16, transform: 'translateY(-50%) rotate(-90deg)',
      };
    } else if (pos === 'left') {
      const topVal = Math.max(16, Math.min(targetRect.top + targetRect.height / 2 - 100, window.innerHeight - tooltipEstH - 16));
      style = {
        position: 'fixed',
        top: topVal,
        right: Math.max(16, window.innerWidth - targetRect.left + gap + pad),
        width: tooltipW,
      };
      const arrowTop = targetRect.top + targetRect.height / 2 - topVal;
      arrowStyle = {
        position: 'absolute', right: -8, top: Math.max(20, Math.min(arrowTop, tooltipEstH - 20)),
        width: 8, height: 16, transform: 'translateY(-50%) rotate(90deg)',
      };
    }
  }

  const progress = totalSteps > 1 ? ((stepIndex + 1) / totalSteps) * 100 : 100;

  return (
    <div ref={tooltipRef} style={{
      ...style,
      background: 'white', borderRadius: 16, padding: 0,
      boxShadow: '0 16px 48px rgba(0,0,0,0.25), 0 4px 16px rgba(0,0,0,0.1)',
      zIndex: 9999,
      animation: 'tourSlideIn 0.35s ease',
      overflow: 'visible',
      maxHeight: 'calc(100vh - 40px)',
    }}>
      {/* Arrow pointer toward target */}
      {arrowStyle && (
        <svg style={arrowStyle} viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 8L8 0L16 8H0Z" fill="white" />
          <path d="M0 8L8 0.5L16 8" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="1" />
        </svg>
      )}
      <div style={{ borderRadius: 16, overflow: 'hidden' }}>
      {/* Progress bar at top */}
      <div style={{ height: 3, background: theme.colors.border }}>
        <div style={{
          height: '100%', width: `${progress}%`,
          background: `linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.primary})`,
          borderRadius: '0 3px 3px 0',
          transition: 'width 0.4s ease',
        }} />
      </div>

      <div style={{ padding: '18px 22px 16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          {/* Step number badge */}
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.primary})`,
            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800,
          }}>
            {step.icon ? <step.icon size={18} /> : stepIndex + 1}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, lineHeight: 1.3 }}>
              {step.title}
            </div>
            <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
              Step {stepIndex + 1} of {totalSteps}
            </div>
          </div>

          <button onClick={onSkip} title="Close tour"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: theme.colors.textLight, borderRadius: 6,
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = theme.colors.text}
            onMouseLeave={e => e.currentTarget.style.color = theme.colors.textLight}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          fontSize: 13, lineHeight: 1.6, color: '#4A5568',
          marginBottom: 16, paddingLeft: 48,
        }}>
          {step.content}
        </div>

        {/* Footer — navigation buttons */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 48 }}>
          <div>
            {isFirst ? (
              <button onClick={onSkip} style={linkBtn()}>
                Skip tour
              </button>
            ) : (
              <button onClick={onPrev} style={linkBtn()}>
                <ChevronLeft size={14} /> Back
              </button>
            )}
          </div>
          <div>
            {isLast ? (
              <button onClick={onDone} style={primaryBtn(theme.colors.success)}>
                Done — Got It!
              </button>
            ) : (
              <button onClick={onNext} style={primaryBtn(theme.colors.accent)}>
                Next <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
      </div>{/* /overflow wrapper */}
    </div>
  );
}


// ── Main GuidedTour Component ────────────────────────────────
export default function GuidedTour({ tourId, steps, autoStart = false, onComplete }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const storageKey = `sabalos_tour_${tourId}`;

  // Auto-start on mount if requested
  useEffect(() => {
    if (autoStart && !window.__tourCompleted?.[tourId]) {
      const timer = setTimeout(() => setIsActive(true), 800);
      return () => clearTimeout(timer);
    }
  }, [tourId, autoStart]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        if (currentStep < steps.length - 1) setCurrentStep(s => s + 1);
        else handleDone();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentStep > 0) setCurrentStep(s => s - 1);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isActive, currentStep, steps.length]);

  // Find and track target element position
  const updateTargetRect = useCallback(() => {
    if (!isActive || !steps[currentStep]) return;

    const step = steps[currentStep];
    if (!step.target) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);

      // Scroll into view if off-screen
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          setTargetRect(el.getBoundingClientRect());
        }, 450);
      }
    } else {
      setTargetRect(null);
    }
  }, [isActive, currentStep, steps]);

  useEffect(() => {
    updateTargetRect();
    // Small delay to re-measure after any layout shifts
    const t = setTimeout(updateTargetRect, 100);
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [updateTargetRect]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(s => s + 1);
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  const handleDone = () => {
    setIsActive(false);
    setCurrentStep(0);
    if (!window.__tourCompleted) window.__tourCompleted = {};
    window.__tourCompleted[tourId] = true;
    onComplete?.();
  };

  const handleSkip = () => handleDone();

  const startTour = () => {
    setCurrentStep(0);
    setIsActive(true);
  };

  // ── Inactive: show floating "Guide Me" button ──
  if (!isActive) {
    return (
      <button
        onClick={startTour}
        title="Guide Me Through This Page"
        style={{
          position: 'fixed', bottom: 80, right: 20, zIndex: 1000,
          padding: '10px 16px', borderRadius: 24,
          background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.primary})`,
          color: 'white', border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: 7,
          fontSize: 13, fontWeight: 600,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.28)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)'; }}
      >
        <BookOpen size={16} /> Guide Me
      </button>
    );
  }

  // ── Active: overlay + tooltip ──
  const step = steps[currentStep];
  if (!step) return null;

  return (
    <>
      <style>{`
        @keyframes tourSlideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <SpotlightOverlay targetRect={targetRect} onClick={() => {}} />

      <TourTooltip
        step={step}
        stepIndex={currentStep}
        totalSteps={steps.length}
        targetRect={targetRect}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onDone={handleDone}
      />
    </>
  );
}


// ── Re-export helper ─────────────────────────────────────────
export function GuideButton({ onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 12px', borderRadius: 7,
      border: `1px solid ${theme.colors.accent}40`,
      background: `${theme.colors.accent}08`,
      color: theme.colors.accent, fontSize: 12, fontWeight: 600,
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
    }}>
      <BookOpen size={13} /> Guide Me
    </button>
  );
}


// ── Style helpers ────────────────────────────────────────────
function linkBtn() {
  return {
    background: 'none', border: 'none', cursor: 'pointer',
    color: theme.colors.textLight, fontSize: 12, fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: 3,
    padding: '6px 8px', borderRadius: 6,
    transition: 'color 0.15s',
  };
}

function primaryBtn(bg) {
  return {
    padding: '8px 18px', borderRadius: 8, border: 'none',
    background: bg, color: 'white', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
    transition: 'transform 0.15s, box-shadow 0.15s',
    boxShadow: `0 2px 8px ${bg}40`,
  };
}
